import React, { useEffect, useRef, useState } from "react";
import {
  Skeleton,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Table } from "reactstrap";
import "./analytics.css";
import NoDataComponent from "../../reusableComponents/noDataComponent/NoDataComponent";
import HoverToDisplayFullTitle from "../../reusableComponents/HoverToDisplayFullTitle";

const TableHeader = styled(TableRow)(({ theme }) => ({
  backgroundColor: "#BAE7FF",
  position: "sticky",
  top: -1,
  zIndex: 1,
  "& th": {
    position: "sticky",
    top: 0,
    backgroundColor: "#BAE7FF",
    zIndex: 2,
  },
}));

function descendingComparator(a, b, orderBy) {
  let oreder_local =
    `${orderBy}_sort` in a && `${orderBy}_sort` in b
      ? `${orderBy}_sort`
      : orderBy;
  if (a?.sort === false || b?.sort === false) return 0;
  if (b[oreder_local] < a[oreder_local]) {
    return -1;
  }
  if (b[oreder_local] > a[oreder_local]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function AnalyticsTable({
  schema,
  data,
  tableTitle,
  logoDict,
  default_orderBy,
  showLogo,
}) {
  const [headerWidth, setHeaderWidth] = useState(0);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState(default_orderBy);
  const tableRef = useRef(null);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  useEffect(() => {
    const handleResize = () => {
      if (tableRef.current) {
        const { width } = tableRef?.current?.getBoundingClientRect();
        setHeaderWidth(width);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const createSortHandler = (property) => (event) => {
    handleRequestSort(event, property);
  };
  const visibleRows = React.useMemo(() => {
    return data && stableSort(data, getComparator(order, orderBy));
  }, [data, order, orderBy]);

  return (
    <>
      {visibleRows ? (
        visibleRows?.length >= 1 ? (
          <TableContainer>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead sx={{ border: "none" }}>
                <TableRow sx={{ border: "none" }}>
                  {schema?.map((column, idxCol) => (
                    <TableCell
                      key={column?.label}
                      sx={{ border: "none !important" }}
                      className="tableHeader"
                      align={column?.align}
                      sortDirection={orderBy === column?.name ? order : false}
                    >
                      {column?.sort === false ? (
                        column?.label
                      ) : (
                        <TableSortLabel
                          active={orderBy === column?.name}
                          direction={orderBy === column?.name ? order : "asc"}
                          onClick={createSortHandler(column?.name)}
                        >
                          {column?.label}
                        </TableSortLabel>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows?.map((row, idxRow) => (
                  <TableRow
                    key={row?.id}
                    style={{
                      height: "45px",
                      width: headerWidth,
                      padding: "12px 0px !important",
                    }}
                  >
                    {schema?.map((column, idxCol) => (
                      <TableCell
                        key={column?.name}
                        nowrap={column?.name === "value"}
                        sx={{
                          border: idxRow === 0 ? "none !important" : "",
                          padding: "0px 12px 12px 0px !important",
                        }}
                        className="tableContent"
                        align={column?.align}
                      >
                        {column?.name === "change" ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              color:
                                parseFloat(row?.change) === 0
                                  ? "#AD4E00"
                                  : parseFloat(row?.change) > 0
                                    ? "#0B6A48"
                                    : "#B9232F",
                            }}
                          >
                            {parseFloat(row?.change) < 0 ? (
                              <img
                                src="/assets/analytics/ArrowDown.svg"
                                alt="same"
                              />
                            ) : parseFloat(row?.change) > 0 ? (
                              <img
                                src="/assets/analytics/ArrowUp.svg"
                                alt="same"
                              />
                            ) : (
                              <img
                                src="/assets/analytics/grip-lines.svg"
                                alt="same"
                              />
                            )}
                            {row?.changeLocale}
                          </div>
                        ) : column?.showLogo ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              background: column?.chipColor,
                              padding: "8px",
                              borderRadius: "4px",
                              width: "fit-content",
                            }}
                          >
                            {showLogo && (
                              <img
                                src={
                                  !logoDict
                                    ? column?.logoDictionary[
                                    row[column?.logoKey]
                                    ][column?.logoDictKey]
                                    : logoDict?.find(
                                      (obj) =>
                                        obj[column?.logoKey] ===
                                        row[column?.logoKey]
                                    )[column?.logoDictKey]
                                }
                                style={{ height: "18px", width: "18px" }}
                              />
                            )}

                            <div>
                              {row[column?.name]}
                              {column?.name === "offersCountLocale" && "%"}
                            </div>
                          </div>
                        ) : (
                          <HoverToDisplayFullTitle
                            title={`${row[column?.name]}${column?.name === "offersCountLocale" ? "%" : ""
                              }`}
                            len={30}
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <NoDataComponent />
          </div>
        )
      ) : (
        <div
          className="table-skeleton"
          style={{ height: "100%", width: "100%" }}
        >
          <Skeleton
            variant="rounded"
            animation="wave"
            width={"100%"}
            height={"100%"}
          />
        </div>
      )}
    </>
  );
}
